// =========================================
// FILE:
// src/pages/dashboard/brsr/PrincipleFormPage.jsx
// =========================================

import {
  useEffect,
  useState
} from "react";

import {
  useNavigate,
  useParams,
  useLocation
} from "react-router-dom";

import Toast from "../../../shared/components/Toast";

import apiClient from "../../../shared/API/apiClient";

import { useReportingYear } from "../../../context/ReportingYearContext";

import { getStoredRole } from "../../../shared/utils/auth";

import PrincipleRenderer from "../../../shared/components/PrincipleRenderer";

import "../../../styles/pages/reviewscreen.css";
import "../../../styles/pages/record_entry_form.css";
import usePermission from "../../../shared/hooks/usePermission";

const PrincipleFormPage = () => {

const { hasPermission } = usePermission();

  // =========================================
  // NAVIGATION
  // =========================================

  const navigate = useNavigate();

  // =========================================
  // LOCATION
  // =========================================

  const location =useLocation();

  const entityName =location.state?.entityName;

  // =========================================
  // PARAMS
  // =========================================

  const {
    principleCode,
    legalEntityId,
    reportingYearId
  } = useParams();

  // =========================================
  // CONTEXT
  // =========================================

  const {
    reportingYear
  } = useReportingYear();

  const role =getStoredRole();

  // =========================================
  // STATES
  // =========================================

  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [
    principleData,
    setPrincipleData
  ] = useState(null);

  const [formData, setFormData] =
    useState({});

  const [errors, setErrors] =
    useState({});

  const [toast, setToast] =
    useState(null);

  const [
    isYearLocked,
    setIsYearLocked
  ] = useState(true);

  const [statusName] =
    useState("");

  // =========================================
  // ACCESS
  // =========================================

  const isEditable =
    (
      role === "Admin" ||
      role === "Super Admin" ||
      role === "Preparer"
    ) &&
    !isYearLocked &&
    (
      statusName?.toLowerCase() ===
        "draft" ||
      statusName === ""
    );

  // =========================================
  // TOAST
  // =========================================

  const showToast = (
    message,
    type = "success"
  ) => {

    setToast({
      message,
      type
    });
  };

  // =========================================
  // NORMALIZE
  // =========================================

  const normalise = (val) =>
    (val ?? "")
      .toString()
      .trim()
      .replace(/^fy/i, "")
      .toLowerCase();

  // =========================================
  // FETCH LOCK STATUS
  // =========================================

  useEffect(() => {

    const fetchYearLockStatus =
      async () => {

        try {

          const res =
            await apiClient.post(
              "/api/reportingyear"
            );

          const years =
            res.data?.data || [];

          const matched =
            years.find(
              (y) =>
                normalise(
                  y.year_label
                ) ===
                normalise(
                  reportingYear
                )
            );

          setIsYearLocked(
            matched
              ? matched.is_locked === 1
              : true
          );

        } catch (err) {

          

          setIsYearLocked(true);
        }
      };

    if (reportingYear) {

      fetchYearLockStatus();
    }

  }, [reportingYear]);

  // =========================================
  // FETCH PRINCIPLE
  // =========================================

  useEffect(() => {

    if (
      !principleCode ||
      !legalEntityId ||
      !reportingYearId
    ) {
      return;
    }

    fetchPrinciple();

  }, [
    principleCode,
    legalEntityId,
    reportingYearId
  ]);

 // =========================================
// FETCH API
// =========================================

const fetchPrinciple = async () => {

  try {

    setLoading(true);

    // =========================
    // QUESTIONS
    // =========================

    const principleResponse =
      await apiClient.get(
        `/api/principles/${principleCode}?legal_entity_id=${legalEntityId}&reporting_year_id=${reportingYearId}`
      );

    setPrincipleData(principleResponse.data);

    // =========================
    // SAVED RESPONSES
    // =========================

    const responseApi =
      await apiClient.get(
        `/api/principles/responses/${principleCode}?legal_entity_id=${legalEntityId}&reporting_year_id=${reportingYearId}`
      );

    const responses =
      responseApi?.data?.data?.responses ||
      responseApi?.data?.responses ||
      [];

    const initialData = {};

    const questions = [
      ...(principleResponse.data?.sections?.essential || []),
      ...(principleResponse.data?.sections?.leadership || [])
    ];

    responses.forEach((item) => {

const question = questions.find(
  q => q.question_code === item.question_code
);

if (question) {

  question.evidence =
    item.evidence || [];

}
      // =================================
      // TABLE
      // =================================

     if (item.input_type === "table") {

          const schema =
            typeof item.ui_schema === "string"
              ? JSON.parse(item.ui_schema)
              : item.ui_schema || {};

          initialData[item.question_code] = {

            rows:
              Array.isArray(item.response_details)
                ? item.response_details
                : (schema.rows || []),

            ...(item.table_meta || {}),

            evidence_files: [],

            existing_evidence:
              item.evidence || []

          };

          return;
        }

      // =================================
      // GROUPED
      // =================================

      if (item.input_type === "grouped") {

        initialData[
          item.question_code
        ] =
          item.response_details || {};

        return;
      }

      // =================================
      // DERIVED
      // =================================
      if (item.input_type === "derived") {

        const schema =
          item.ui_schema || {};

          // =====================================
          // P6-Q1 AUTO SUMMARY
          // =====================================

          // AUTO SUMMARY QUESTIONS

          if (schema.type === "auto_summary") {

            initialData[item.question_code] = {
              ...(item.response_details || {})
            };

            return;
          }
          // =====================================
          // INLINE DERIVED (P1/P2/P8)
          // =====================================

          if (
            schema.fields?.length > 0 &&
            !schema.fields.some(
              (f) => f.source_question_code
            )
          ) {

            initialData[
              item.question_code
            ] =
              item.system_value !== null &&
              item.system_value !== undefined
                ? Number(item.system_value)
                : "";

            // RESTORE INLINE FIELDS

            schema.fields.forEach(
              (field) => {

                initialData[
                  `${item.question_code}.${field.key}`
                ] =
                  item.response_details?.[
                    field.key
                  ] ?? "";

              }
            );

            return;
          }

            // =====================================
            // LINKED + AUTO DERIVED
            // =====================================

            initialData[
              item.question_code
            ] =
              item.system_value !== null &&
              item.system_value !== undefined
                ? Number(item.system_value)
                : "";

            return;
          }

      // =================================
      // TEXTAREA
      // =================================

      if (
        item.ui_schema?.type ===
        "textarea"
      ) {

        initialData[
          item.question_code
        ] = {

          text:
            item.value || "",

          evidence_files: [],

          existing_evidence:
            item.evidence || []

        };

        return;
      }

      // =================================
      // BOOLEAN
      // =================================

      if (
        item.data_type ===
        "boolean"
      ) {

        initialData[
          item.question_code
        ] =
          item.value === "1" ||
          item.value === true;

        return;
      }

      // =================================
      // NORMAL
      // =================================

      initialData[
        item.question_code
      ] =
        item.value ?? "";

    });

    // =========================================
    // P6 AUTO CALCULATED VALUES
    // =========================================

    if (principleCode === "P6") {

      // ================================
      // ENVIRONMENT SUMMARY
      // ================================

      try {

        const envResponse =
          await apiClient.get(
            `/api/p6-environmental-summary?reporting_year_id=${reportingYearId}&legal_entity_id=${legalEntityId}`
          );

        const envData =
            envResponse.data || {};

          // P6-Q1

          initialData["energy_breakdown"] = {
            electricity_consumption_gj:
              Number(envData.electricity_consumption_gj || 0),

            fuel_consumption_gj:
              Number(envData.fuel_consumption_gj || 0),

            other_energy_sources_gj:
              Number(envData.other_energy_sources_gj || 0),

            total_energy_consumption_gj:
              Number(envData.total_energy_consumption_gj || 0),

            renewable_energy_gj:
              Number(envData.renewable_energy_gj || 0),

            non_renewable_energy_gj:
              Number(envData.non_renewable_energy_gj || 0)
          };

          initialData["ghg_breakdown"] = {
                      scope1_emissions:
                        Number(envData.scope1_emissions || 0),

                      scope2_emissions:
                        Number(envData.scope2_emissions || 0),

                      total_emissions:
                        Number(envData.total_emissions || 0)
                    };

                } catch (envError) {

                  console.error(
                    "ENV API ERROR",
                    envError
                  );

                }

        // ================================
        // WATER WITHDRAWAL SUMMARY
        // ================================

        try {

          const waterResponse =
            await apiClient.get(
              `/api/p6-water-withdrawal-summary?reporting_year_id=${reportingYearId}&legal_entity_id=${legalEntityId}`
            );

          const waterData =
            waterResponse.data || {};

         initialData["water_breakdown"] = {
          surface_water_kl: Number(waterData.surface_water_kl || 0),
          groundwater_kl: Number(waterData.groundwater_kl || 0),
          third_party_water_kl: Number(waterData.third_party_water_kl || 0),
          seawater_desalinated_kl: Number(waterData.seawater_desalinated_kl || 0),
          others_kl: Number(waterData.others_kl || 0),
          total_water_withdrawal_kl: Number(waterData.total_water_withdrawal_kl || 0),
          total_water_consumption_kl: Number(waterData.total_water_consumption_kl || 0)
        };

        } catch (waterError) {

          console.error(
            "WATER API ERROR",
            waterError
          );

        }
        // ================================
        // P6-Q8 WASTE MANAGEMENT
        // ================================

        try {

          const wasteResponse =
            await apiClient.get(
              `/api/p6-waste-management-summary?reporting_year_id=${reportingYearId}&legal_entity_id=${legalEntityId}`
            );

          const wasteData =
            wasteResponse.data || {};

          initialData["waste_breakdown"] = {

            plastic_waste_mt:
              Number(wasteData.plastic_waste_mt || 0),

            ewaste_mt:
              Number(wasteData.ewaste_mt || 0),

            biomedical_waste_mt:
              Number(wasteData.biomedical_waste_mt || 0),

            construction_demolition_waste_mt:
              Number(wasteData.construction_demolition_waste_mt || 0),

            battery_waste_mt:
              Number(wasteData.battery_waste_mt || 0),

            radioactive_waste_mt:
              Number(wasteData.radioactive_waste_mt || 0),

            other_hazardous_waste_mt:
              Number(wasteData.other_hazardous_waste_mt || 0),

            other_non_hazardous_waste_mt:
              Number(wasteData.other_non_hazardous_waste_mt || 0),

            total_waste_generated_mt:
              Number(wasteData.total_waste_generated_mt || 0),

            recycled_mt:
              Number(wasteData.recycled_mt || 0),

            reused_mt:
              Number(wasteData.reused_mt || 0),

            other_recovery_operations_mt:
              Number(wasteData.other_recovery_operations_mt || 0),

            total_recovered_mt:
              Number(wasteData.total_recovered_mt || 0),

            incineration_mt:
              Number(wasteData.incineration_mt || 0),

            landfilling_mt:
              Number(wasteData.landfilling_mt || 0),

            other_disposal_operations_mt:
              Number(wasteData.other_disposal_operations_mt || 0),

            total_disposed_mt:
              Number(wasteData.total_disposed_mt || 0)

          };

        } catch (wasteError) {

          console.error(
            "P6 WASTE MANAGEMENT API ERROR",
            wasteError
          );

        }

    }

          // =========================================
          // FINAL FORM DATA
          // =========================================

          setFormData({

            ...initialData
          });

        } catch (err) {

          console.error(
            "FETCH PRINCIPLE ERROR",
            err
          );

        } finally {

          setLoading(false);
        }
      };

  // =========================================
  // FIELD CHANGE
  // =========================================
const handleFieldChange = (
  questionCode,
  value
) => {

  if (!isEditable) {
    return;
  }

  setFormData((prev) => {

    const updated = {

      ...prev,

      [questionCode]: value
    };

// =========================
// RECALCULATE DERIVED
// =========================

const allQuestions = [

  ...(principleData?.sections
    ?.essential || []),

  ...(principleData?.sections
    ?.leadership || [])

];

allQuestions.forEach((question) => {

  if (
    question.input_type !==
    "derived"
  ) {

    return;
  }

  const schema =
    question.ui_schema || {};

  // ====================================
  // SKIP AUTO DERIVED ONLY
  // ====================================

  if (
    !schema.fields ||
    schema.fields.length === 0
  ) {

    return;
  }

  try {

    let formula =
      schema.formula || "";

    schema.fields.forEach(
      (field) => {

        let sourceValue = 0;

        // ====================================
        // LINKED DERIVED
        // ====================================

       if (schema.source_question_code) {

              const sourceRows =
                formData[
                  schema.source_question_code
                ]?.rows || [];

              sourceValue =
                sourceRows.reduce(
                  (sum, row) => {

                    return (
                      sum +
                      Number(
                        row?.[field.key] || 0
                      )
                    );

                  },
                  0
                );

      }

        // ====================================
        // INLINE DERIVED
        // ====================================

        else {

          sourceValue =
            Number(
              updated[
                `${question.question_code}.${field.key}`
              ] || 0
            );

        }

        const variableName =
  field.alias || field.key;

formula = formula.replace(
  new RegExp(`\\b${variableName}\\b`, "g"),
  sourceValue
);

      }
    );

    let calculated =
      eval(formula);

    if (
      isNaN(calculated) ||
      !isFinite(calculated)
    ) {

      calculated = "";

    } else {

      calculated =
        Number(
          calculated.toFixed(2)
        );
    }

    updated[
      question.question_code
    ] = calculated;

  } catch (err) {

    console.error(
      "DERIVED CALCULATION ERROR",
      err
    );

  }

});

    return updated;
  });

  setErrors((prev) => ({

    ...prev,

    [questionCode]: ""
  }));
};


  // =========================================
  // BUILD PAYLOAD
  // =========================================
const buildFormDataPayload = (isSubmit = false) => {

  const form=new FormData();

  form.append("legal_entity_id",legalEntityId);

  form.append("reporting_year_id",reportingYearId);

  const allQuestions=[
    ...(principleData?.sections?.essential || []),
    ...(principleData?.sections?.leadership || [])
  ];

  allQuestions.forEach((question,index)=>{

    const answer=formData[question.question_code];

    form.append(`responses[${index}][question_id]`,question.question_id);


if (question.ui_schema?.type === "auto_summary") {

  const systemKey =
    question.ui_schema?.system_value_key;

  const summarySource =
    question.ui_schema?.summary_source;

  const sourceData =
    formData?.[summarySource] || {};

console.log(
  "FORMDATA GHG",
  formData.ghg
);

console.log(
  "FULL FORMDATA",
  formData
);
  console.log(
    question.question_code,
    "SUMMARY SOURCE",
    summarySource
  );

  console.log(
    question.question_code,
    "SOURCE DATA",
    sourceData
  );

  let systemValue = 0;

  if (systemKey) {
    systemValue = Number(
      sourceData?.[systemKey] || 0
    );
  }

  form.append(
    `responses[${index}][system_value]`,
    systemValue
  );

  form.append(
    `responses[${index}][value]`,
    ""
  );

  (question.ui_schema?.rows || []).forEach((row) => {

    if (row.field_key) {

      form.append(
        `responses[${index}][response_details][${row.field_key}]`,
        answer?.[row.field_key] || ""
      );

    }

    if (row.source_key) {

      console.log(
        row.source_key,
        sourceData?.[row.source_key]
      );

      form.append(
        `responses[${index}][response_details][${row.source_key}]`,
        sourceData?.[row.source_key] ?? ""
      );

    }

  });

  return;
}

    // =====================================
    // SINGLE
    // =====================================

    if(question.input_type==="single"){

      let value=answer;

      if(question.data_type==="boolean"){

        value=answer ? 1 : 0;

      }else if(
        question.ui_schema?.type==="textarea" ||
        question.ui_schema?.multiline===true
      ){

        value=answer?.text || "";

      }else{

        value=answer ?? "";
      }

      form.append(`responses[${index}][value]`,value);

      form.append(`responses[${index}][system_value]`,"");

      answer?.evidence_files?.forEach(file=>{

        form.append(
          `responses[${index}][evidence_files][]`,
          file
        );

      });

      return;
    }

    // =====================================
    // GROUPED
    // =====================================

    if(question.input_type==="grouped"){

      const groupedAnswer={...(answer || {})};

      delete groupedAnswer.rows;

      delete groupedAnswer.evidence_files;

      let systemValue=null;

      try{

        const formula=
          question.ui_schema?.derived?.formula ||
          question.ui_schema?.formula;

        if(formula){

          let computed=formula;

          question.ui_schema.fields?.forEach(field=>{

            computed=computed.replace(
              new RegExp(`\\b${field.key}\\b`,"g"),
              Number(groupedAnswer?.[field.key] || 0)
            );

          });

          systemValue=eval(computed);

          if(isNaN(systemValue)||!isFinite(systemValue)){

            systemValue=null;

          }else{

            systemValue=Number(systemValue.toFixed(2));
          }
        }

      }catch{

        systemValue=null;
      }

      form.append(`responses[${index}][value]`,"");

      if (systemValue !== null && systemValue !== undefined && systemValue !== "") {

        console.log(
  question.question_code,
  groupedAnswer,
  systemValue
);

              form.append(
                `responses[${index}][system_value]`,
                systemValue
              );

            }
        // =====================================
        // APPEND DERIVED FIELD VALUE
        // =====================================

        if(question.ui_schema?.derived?.key){

          form.append(
            `responses[${index}][response_details][${question.ui_schema.derived.key}]`,
            systemValue ?? ""
          );
        }

        question.ui_schema.fields?.forEach(field=>{

          form.append(
            `responses[${index}][response_details][${field.key}]`,
            groupedAnswer[field.key] ?? ""
          );

        });

      question.ui_schema.fields?.forEach(field=>{

        form.append(
          `responses[${index}][response_details][${field.key}]`,
          groupedAnswer[field.key] ?? ""
        );

      });

      return;
    }

  if(question.input_type==="table"){

  const schema=
    typeof question.ui_schema==="string"
      ? JSON.parse(question.ui_schema)
      : question.ui_schema || {};

  form.append(
    `responses[${index}][value]`,
    ""
  );

  form.append(
    `responses[${index}][system_value]`,
    ""
  );


  const rows =
  (answer?.rows || []).filter(
    row => row.type !== "section"
  );

rows.forEach((row,rowIndex)=>{

    const processedRow={...row};

    // =====================================
    // DERIVED COLUMNS
    // =====================================

    schema.columns?.forEach(column=>{

      if(
        column.type==="derived" &&
        column.formula
      ){

        try{

          let formula=column.formula;

          schema.columns.forEach(col=>{

            formula=formula.replace(
              new RegExp(`\\b${col.key}\\b`,"g"),
              Number(processedRow[col.key] || 0)
            );

          });


          let calculatedValue=eval(formula);


          if(
            isNaN(calculatedValue) ||
            !isFinite(calculatedValue)
          ){

            calculatedValue="";

          }else{

            calculatedValue=Number(
              calculatedValue.toFixed(2)
            );
          }

          processedRow[column.key]=calculatedValue;

         

        }catch(err){

          

          processedRow[column.key]="";
        }
      }

    });

    Object.entries(processedRow).forEach(([key,value])=>{

      form.append(
        `responses[${index}][response_details][${rowIndex}][${key}]`,
        value ?? ""
      );

    });

  });
// ADD THIS
schema.rows?.forEach((schemaRow) => {

  if (schemaRow.field_key) {

    form.append(
      `responses[${index}][response_details][${schemaRow.field_key}]`,
      answer?.[schemaRow.field_key] || ""
    );

  }

});
// =====================================
// NOTE FIELD
// =====================================

if (schema.note_field?.field_key) {

  form.append(
    `responses[${index}][response_details][${schema.note_field.field_key}]`,
    answer?.[schema.note_field.field_key] || ""
  );

}
if (answer?.remove_evidence) {

    form.append(
        `responses[${index}][remove_evidence]`,
        1
    );

}
  if(answer?.evidence_files?.length>0){

    answer.evidence_files.forEach(file=>{

      form.append(
        `responses[${index}][evidence_files][]`,
        file
      );

    });

  }

  return;
}

    // =====================================
    // DERIVED
    // =====================================

    if(question.input_type==="derived"){

      const schema=question.ui_schema || {};

      // =====================================
      // P6-Q4 HARD FIX
      // =====================================

      if(question.question_code==="P6-Q4"){

        const scope1=Number(formData["P6-Q2"] || 0);

        const scope2=Number(formData["P6-Q3"] || 0);

        const energy=Number(formData["P6-Q1"] || 0);

        const calculatedValue=
          energy>0
            ? Number(
                (((scope1+scope2)/energy)).toFixed(2)
              )
            : "";

        form.append(`responses[${index}][value]`,"");

        form.append(
          `responses[${index}][system_value]`,
          calculatedValue
        );

        return;
      }

      let calculatedValue=null;

      // =====================================
      // AUTO DERIVED
      // =====================================

      if(!schema.fields || schema.fields.length===0){

        form.append(`responses[${index}][value]`,"");

        form.append(
          `responses[${index}][system_value]`,
          answer ?? ""
        );

        return;
      }

      try{

        let formula=schema.formula || "";

        schema.fields.forEach(field=>{

          let sourceValue=0;

          // LINKED DERIVED

          if(schema.source_question_code){

            const sourceRows=
              formData[schema.source_question_code]?.rows || [];

            sourceValue=sourceRows.reduce((sum,row)=>{

              return sum+Number(row?.[field.key] || 0);

            },0);

          }

          // INLINE DERIVED

          else{

            sourceValue=Number(
              formData[
                `${question.question_code}.${field.key}`
              ] || 0
            );

            form.append(
              `responses[${index}][response_details][${field.key}]`,
              sourceValue
            );
          }

          const variableName=field.alias || field.key;

          formula=formula.replace(
            new RegExp(`\\b${variableName}\\b`,"g"),
            sourceValue
          );

        });

        calculatedValue=eval(formula);

        if(isNaN(calculatedValue)||!isFinite(calculatedValue)){

          calculatedValue=null;

        }else{

          calculatedValue=Number(
            calculatedValue.toFixed(2)
          );
        }

      }catch(err){

        console.error("DERIVED ERROR",err);

        calculatedValue=null;
      }

      form.append(`responses[${index}][value]`,"");

      form.append(
        `responses[${index}][system_value]`,
        calculatedValue ?? ""
      );

      return;
    }

  });

  return form;
};


  // =========================================
  // VALIDATION
  // =========================================
const validateBeforeSubmit = () => {

  const validationErrors = {};

  const allQuestions = [

    ...(principleData?.sections?.essential || []),

    ...(principleData?.sections?.leadership || [])

  ];

  const isEmptyValue = (val) => {

    // NULL / UNDEFINED

    if (
      val === undefined ||
      val === null
    ) {

      return true;
    }

    // STRING

    if (
      typeof val === "string"
    ) {

      return val.trim() === "";
    }

    // ARRAY

    if (
      Array.isArray(val)
    ) {

      return val.length === 0;
    }

    // OBJECT

    if (
      typeof val === "object"
    ) {

      return !Object.values(val).some(
        (v) =>
          v !== null &&
          v !== undefined &&
          String(v).trim() !== ""
      );
    }

    return false;
  };

const isValidUrl = (url) => {

  if (!url || String(url).trim() === "") {
    return true; // handled by required validation
  }

  try {

    new URL(url);

    return true;

  } catch {

    return false;
  }
};
  allQuestions.forEach((question) => {

    const schema =
      question.ui_schema || {};

    // =============================
    // URL VALIDATION
    // =============================

if (
  schema.type === "url"
) {

  const urlValue =
    formData[
      question.question_code
    ];

  if (
    urlValue &&
    !isValidUrl(urlValue)
  ) {

    validationErrors[
      question.question_code
    ] =
      "Please enter a valid URL";
  }
}

    let actualValue =
      formData[
        question.question_code
      ];
      // =============================
      // GROUPED URL VALIDATION
      // =============================

          if (
            question.input_type === "grouped"
          ) {

            question.ui_schema?.fields?.forEach(
              (field) => {

                if (
                  field.type === "url"
                ) {

                  const urlValue =
                    actualValue?.[field.key];

                  if (
                    urlValue &&
                    !isValidUrl(urlValue)
                  ) {

                    validationErrors[
                      question.question_code
                    ] =
                      `${field.label} must be a valid URL`;
                  }
                }

              }
            );
          }
    // =============================
    // TEXTAREA
    // =============================

    if (
      schema.type === "textarea"
    ) {

      actualValue =
        actualValue?.text || "";
    }

   
              // =============================
              // TABLE URL VALIDATION
              // =============================

              if (
                question.input_type === "table"
              ) {

                const rows =
                  actualValue?.rows || [];

                const urlColumns =
                  question.ui_schema?.columns?.filter(
                    col => col.type === "url"
                  ) || [];

                rows.forEach((row) => {

                  urlColumns.forEach((column) => {

                    const urlValue =
                      row?.[column.key];

                    if (
                      urlValue &&
                      !isValidUrl(urlValue)
                    ) {

                      validationErrors[
                        question.question_code
                      ] =
                        `${column.label} must be a valid URL`;
                    }

                  });

                });
              }

    // =============================
    // DERIVED
    // =============================

    if (
      question.input_type === "derived"
    ) {

      return;
    }

    // =============================
        // REQUIRED
        // =============================

        if (
          question.is_required
        ) {

          if (
            isEmptyValue(actualValue)
          ) {

            validationErrors[
              question.question_code
            ] =
              "This field is required";
          }

        }

        // =============================
        // EVIDENCE REQUIRED
        // =============================

       if (
            question.upload_evidence === 1 &&
            question.evidence_required === 1
          ) {

            const evidenceFiles =
              formData[
                question.question_code
              ]?.evidence_files || [];

            const existingEvidence =
              question.evidence || [];

            if (
              evidenceFiles.length === 0 &&
              existingEvidence.length === 0
            ) {

              validationErrors[
                question.question_code
              ] =
                "Supporting evidence is required";
            }

          }

  });

  setErrors(validationErrors);

  return {
  isValid:
    Object.keys(validationErrors).length === 0,
  errors: validationErrors
};
};


  // =========================================
  // SUBMIT
  // =========================================
const handleSubmit = async () => {

          const validationResult =
  validateBeforeSubmit();

if (!validationResult.isValid) {

  const firstError =
    Object.entries(
      validationResult.errors
    )[0];

  showToast(
    `${firstError[0]} - ${firstError[1]}`,
    "error"
  );

  return;
}

  try {

    setSaving(true);

    // ===================================
    // SAVE RESPONSES
    // ===================================

    const payload =
  buildFormDataPayload(true);
    await apiClient.post(
      "/api/principles/save-allresponses",
      payload,
      {
        headers: {
          "Content-Type":
            "multipart/form-data"
        }
      }
    );

    // ===================================
    // SUBMIT PRINCIPLE
    // ===================================

    const submitResponse =
      await apiClient.post(
        "/api/principles/submit",
        {

          principle_id:
            principleData?.principle
              ?.principle_id,

          legal_entity_id:
            legalEntityId,

          reporting_year_id:
            reportingYearId

        }
      );

    // ===================================
    // SUCCESS
    // ===================================

    if (
      submitResponse?.data?.success
    ) {

      showToast(
        "Principle submitted successfully",
        "success"
      );

      setTimeout(() => {

        navigate(
          "/dashboard/principle-dashboard",
          {
            state: {
              entityId:
                location.state?.entityId,
            },
          }
        );

      }, 1200);
    }

  } catch (err) {

    showToast(
      err?.response?.data?.message ||
      "Submission failed",
      "error"
    );

  } finally {

    setSaving(false);
  }
};


  // =========================================
  // SAVE DRAFT
  // =========================================

  const handleSaveDraft = async () => {

    try {

      setSaving(true);

      const payload =
  buildFormDataPayload(false);

      await apiClient.post(
        "/api/principles/save-allresponses",
        payload,
        {
          headers: {
            "Content-Type":
              "multipart/form-data"
          }
        }
      );

      showToast(
        "Draft saved successfully",
        "success"
      );

    } catch (err) {

      

      showToast(
        err?.response?.data?.message ||
        "Draft save failed",
        "error"
      );

    } finally {

      setSaving(false);
    }
  };

  // =========================================
  // LOCK REASON
  // =========================================

  const lockReason = () => {

    if (!hasPermission("principle_dashboard", "add")) {
  return "You do not have permission to add/edit this activity.";
}

    if (isYearLocked) {

      return `Reporting year ${reportingYear} is locked`;
    }

    if (
      statusName?.toLowerCase() !==
        "draft" &&
      statusName !== ""
    ) {

      return `Already submitted — status is "${statusName}"`;
    }

    return "Read-only";
  };

  // =========================================
  // LOADING
  // =========================================
if (isYearLocked) {
  return (
    <div className="dash-page">
      <div className="empty-entity">
        <div className="empty-entity__icon">
          <i className="fa-solid fa-lock" />
        </div>

        <div className="empty-entity__title">
          Reporting Year Closed
        </div>

        <div className="empty-entity__sub">
          Reporting year {reportingYear} is closed. You cannot create or modify principle disclosures.
        </div>
      </div>
    </div>
  );
}

if (!hasPermission("principle_dashboard", "add")) {
  return (
    <div className="dash-page">
      <div className="empty-entity">
        <div className="empty-entity__icon">
          <i className="fa-solid fa-lock" />
        </div>

        <div className="empty-entity__title">
          Access Restricted
        </div>

        <div className="empty-entity__sub">
          You do not have permission to create principle disclosures.
        </div>
      </div>
    </div>
  );
}
  if (loading) {

    return (

      <div className="dash-loader-wrapper">

        <div className="dash-loader"></div>

        <p className="dash-loader-text">
          Loading principle questions...
        </p>

      </div>

    );
  }

  // =========================================
  // RENDER
  // =========================================

  return (

    <>

      <div className="dash-page">

        {/* HEADER */}

        <div className="dash-page-header">

          <div>

            <div className="dash-page-subtitle">

              {
                principleData
                  ?.principle
                  ?.principle_code
              }

            </div>

            <div className="dash-page-title">

              {
                principleData
                  ?.principle
                  ?.principle_name
              }

            </div>

            <div className="dash-page-description">

              {
                principleData
                  ?.principle
                  ?.description
              }

            </div>

            <div className="principle-meta-info">

              <div className="principle-meta-item">

                <span className="principle-meta-label">
                  Reporting Year:
                </span>

                <span className="principle-meta-value">
                  {reportingYear}
                </span>

              </div>

              <div className="principle-meta-item">

                <span className="principle-meta-label">
                  Legal Entity:
                </span>

                <span className="principle-meta-value">
                  {entityName || "N/A"}
                </span>

              </div>

            </div>

          </div>

          <button
            className="org-btn-save"
            onClick={() =>
              navigate(
                "/dashboard/principle-dashboard",
                {
                  state: {
                    entityId:
                      location.state?.entityId,
                  },
                }
              )
            }
          >
            ← Back to Principles
          </button>

        </div>

        {/* STATUS */}

        {!isEditable ? (

          <div
            className="draft-box"
            style={{
              background:
                "#fff3cd",
              color:
                "#856404",
              marginBottom:
                "20px",
              padding:
                "10px 14px",
              borderRadius:
                "8px",
              fontSize:
                "13px"
            }}
          >
            🔒 Read-only —
            {" "}
            {lockReason()}
          </div>

        ) : (

          <div className="draft-box">
            ✏️ Draft — Editable
          </div>

        )}

        {/* FORM */}

        <PrincipleRenderer
          sections={
            principleData
              ?.sections
          }
          formData={
            formData
          }
          errors={errors}
          setErrors={
            setErrors
          }
          onChange={
            handleFieldChange
          }
          isEditable={
            isEditable
          }
        />


        {/* FOOTER */}

        {isEditable && (

          <div className="ecf-footer"
            style={{
              justifyContent:
                "space-between"
            }}
          >

            {/* LEFT */}

            <button
              type="button"
              className="back_button"
              onClick={() =>
                navigate(
                  "/dashboard/principle-dashboard",
                  {
                    state: {
                      entityId:
                        location.state?.entityId,
                    },
                  }
                )
              }
            >
              ← Back to Principles
            </button>

            {/* RIGHT */}

            <div
              style={{
                display: "flex",
                gap: "12px"
              }}
            >

              <button
                type="button"
                className="org-btn-cancel"
                disabled={saving}
                onClick={handleSaveDraft}
              >
                {saving
                  ? "Saving..."
                  : "Save Draft"}
              </button>

              <button
                type="button"
                className="org-btn-save"
                disabled={saving}
                onClick={handleSubmit}
              >
                {saving
                  ? "Submitting..."
                  : "Submit for Review"}
              </button>

            </div>

          </div>

        )}

      </div>

      {/* TOAST */}

      {toast && (

        <Toast
          message={
            toast.message
          }
          type={
            toast.type
          }
          onClose={() =>
            setToast(null)
          }
        />

      )}

    </>

  );
};

export default PrincipleFormPage;